import {
  AccountManager,
  actions,
  constants,
  deleteCookie,
  GLOBAL_ERROR_CODE,
  isEmptyCollection,
  NotificationManager,
  SessionStorageManager,
  SettingsManager,
  SiteConfigManager,
  StorageManager,
} from '@atlas/core';
import { currencySymbols } from '@atlas/ui';

import {
  ACCOUNT_STATUSES as accountStatus,
  HIDE_FREEBET_FOR_CURRENT_SESSION,
  KYC_AUTO_RUN_KEY,
  SIGN_UP_POPUP_STORAGE_KEY,
} from 'common/constants';
import { displayMultiPopup, getMultiPopupConfig } from 'common/Popup/MultiPopup/MultiPopupManager';
import { getTerminalLoginConfig } from 'common/Popup/TerminalLoginPopup/TerminalLoginPopupManager';
import resolveSelfRegulationPopupVisibility from 'src/components/SelfRegulationProvider/resolveSelfRegulationPopupVisibility';
import resolveSpentLimitPopupVisibility from 'src/components/SelfRegulationProvider/resolveSpentLimitPopupVisibility';

const { AFFILIATE_PARTNER_COOKIE_NAME } = constants;

export default (Bus, history, customListeners = {}) => {
  /*
    Check if bus already got listeners
  */
  if (Bus.listenerCount('newListener') > 0) {
    return;
  }

  // Prioritized login handler
  Bus.prependListener(Bus.events.auth.login, () => {
    // Reset freebet reminder settings
    StorageManager.removeItem(HIDE_FREEBET_FOR_CURRENT_SESSION);
  });

  // Loading client preferences on auth
  Bus.on(Bus.events.auth.resolved, () => {
    AccountManager.loadClientPreferences();
  }).on(Bus.events.auth.login, () => {
    AccountManager.loadClientPreferences();
  });

  Bus.on(Bus.events.profile.preferencesUpdate, ({ data: preferences = {} }) => {
    const settingsUpdate = {};

    if (preferences.acceptOddsChanges) {
      settingsUpdate.acceptOddsChanges = preferences.acceptOddsChanges;
    }

    if (preferences.oddsFormat) {
      settingsUpdate.oddsFormat = preferences.oddsFormat;
    }

    if (!isEmptyCollection(settingsUpdate)) {
      SettingsManager.updateSettings(settingsUpdate);
    }
  });

  Bus.on(Bus.events.auth.unresolved, error => {
    // If no error provided
    // Then token not exist at all
    if (!error) {
      return;
    }

    NotificationManager.display({
      message: 'your_session_expired',
      title: 'Authorization',
      type: 'notification',
    });
  });

  Bus.on(Bus.events.profile.realityCheck, () => {
    NotificationManager.display({
      id: 'realityCheck',
      message: 'reality_check_popup_message',
      status: 'popup',
      title: 'reality_check_popup_title',
      type: 'realityCheck',
    });
  });

  Bus.on(Bus.events.auth.login, ({ client }) => {
    const sigapLimitedAt = client.additionalParameters?.SIGAP_ACCESS_LIMITED_AT;

    if (sigapLimitedAt) {
      NotificationManager.display({
        id: 'accountBlocked',
        type: 'accountBlocked',
      });
    }

    StorageManager.setItem('leavePopupShown', 'shown');

    Promise.allSettled([
      resolveSpentLimitPopupVisibility(client, true),
      resolveSelfRegulationPopupVisibility(client, true),
    ]).then(([shouldShowSpentLimit, shouldShowRealityCheck]) => {
      SessionStorageManager.setItem('showSpentLimitPopup', shouldShowSpentLimit ? 'yes' : 'no');
      SessionStorageManager.setItem('showRealityCheckPopup', shouldShowRealityCheck ? 'yes' : 'no');
    });
  });

  Bus.on(Bus.events.auth.formRegistrationSuccess, async credentials => {
    StorageManager.setItem(KYC_AUTO_RUN_KEY, '1');
    StorageManager.setItem('leavePopupShown', 'shown');
    deleteCookie(AFFILIATE_PARTNER_COOKIE_NAME);

    if (customListeners[Bus.events.auth.formRegistrationSuccess]) {
      return customListeners[Bus.events.auth.formRegistrationSuccess](credentials);
    }

    const popupList = [];

    if (credentials.showPopup) {
      popupList.push({
        data: credentials,
        type: 'oneClickSignUp',
      });
    }

    if (SiteConfigManager.getConfig('config.config.registration.showBonusPopup')) {
      popupList.push({
        data: {},
        type: 'registrationBonus',
      });
    } else {
      const terminalLoginPopupConfig = getTerminalLoginConfig();
      if (terminalLoginPopupConfig) {
        popupList.push(terminalLoginPopupConfig);
      }

      const signupMultiPopupConfig = getMultiPopupConfig('sign_up');
      if (signupMultiPopupConfig) {
        const popupListConfig = {
          customDisplay: () => displayMultiPopup('sign_up', SIGN_UP_POPUP_STORAGE_KEY),
          data: signupMultiPopupConfig,
          id: 'multiPopup',
          type: 'multiPopup',
        };

        if (popupList.length) {
          popupList.push(popupListConfig);
        } else {
          popupListConfig.customDisplay();
        }
      }
    }

    // Add ability for each popup to open next one on close current
    const displayNextPopup = nextPopup => {
      if (nextPopup.customDisplay) {
        nextPopup.customDisplay();
      } else {
        setTimeout(() => NotificationManager.display(nextPopup), nextPopup.timeout || 0);
      }
    };

    popupList.forEach((popup, index) => {
      const nextPopup = popupList[index + 1];

      popup.data.onClose = nextPopup ? () => displayNextPopup(nextPopup) : () => {};
    });

    if (popupList[0]) {
      NotificationManager.display(popupList[0]);
    }
  });

  Bus.on(Bus.events.auth.logout, ({ withoutRedirect } = {}) => {
    if (AccountManager.account?.status === accountStatus.PARTIALLY_REGISTERED) {
      StorageManager.removeItem('leavePopupShown');
    }

    NotificationManager.removeNotification('depositSuccess');
    if (!withoutRedirect) {
      history.push(history.location.pathname);
    }

    // NotificationManager.notify({
    //   type: 'notification',
    //   title: 'Authorization',
    //   message: 'You have logged out'
    // })
  });

  // Cleaning main betslip bet amount on successful place bet
  Bus.on(Bus.events.bet.placedSuccess, () => {
    Bus.emit('betslip:main', {
      payload: { amount: undefined },
      type: actions.BETSLIP_STATE_UPDATE,
    });
  });

  // Cleaning main betslip bet amount onсу vip bet placed
  Bus.on(Bus.events.bet.vipPlaced, () => {
    Bus.emit('betslip:main', {
      payload: { amount: undefined },
      type: actions.BETSLIP_STATE_UPDATE,
    });
  });

  Bus.on(Bus.events.bet.editSuccess, () => {
    NotificationManager.display({
      message: 'edit_bet_success_title',
      status: 'success',
      type: 'notification',
    });
  });

  // Vip bets acceptance notification
  Bus.on(Bus.events.bet.vipPlacedSuccess, () => {
    NotificationManager.display({
      message: 'vip_place_bet_accepted',
      status: 'success',
      type: 'notification',
    });
  });

  Bus.on(Bus.events.bet.vipPlacedFail, () => {
    NotificationManager.display({
      message: 'vip_place_bet_rejected',
      status: 'error',
      type: 'notification',
    });
  });

  // Full cashout

  Bus.on(Bus.events.history.cashoutSuccess, data => {
    const currencySymbol = currencySymbols[data.betMoney.currency] || data.betMoney.currency;

    NotificationManager.notify({
      data: {
        currency: currencySymbol,
        value: data.betMoney.betAmount,
      },
      message: 'cashout_success_message',
      status: 'success',
      type: 'notification',
    });
  });

  Bus.on(Bus.events.history.cashoutError, error => {
    const { code = GLOBAL_ERROR_CODE.INTERNAL_ERROR } = error;

    NotificationManager.display({
      message: `cashout_error_${code}`,
      status: 'error',
      type: 'notification',
    });
  });

  // Partial cashout

  Bus.on(Bus.events.history.partialCashoutSuccess, data => {
    const currencySymbol = currencySymbols[data.betMoney.currency] || data.betMoney.currency;

    NotificationManager.notify({
      data: {
        currency: currencySymbol,
        value: data.betMoney.betAmount,
      },
      id: 'partial_cashout',
      message: 'partial_cashout_success_message',
      status: 'success',
      type: 'notification',
    });
  });

  Bus.on(Bus.events.history.partialCashoutError, error => {
    const { code = GLOBAL_ERROR_CODE.INTERNAL_ERROR } = error;

    NotificationManager.display({
      message: `partial_cashout_error_${code}`,
      status: 'error',
      type: 'notification',
    });
  });

  // Order full cashout

  Bus.on(Bus.events.history.orderCashoutError, error => {
    if (error.cashOutType === 'PARTIAL_CASH_OUT') {
      NotificationManager.display({
        message: 'order_partial_cashout_error_message',
        status: 'error',
        type: 'notification',
      });
    } else {
      NotificationManager.display({
        data: { ...error, amount: error.violationInfo.estimatedCashOutAmount / 100 },
        message: 'order_cashout_error_notification',
        status: 'error',
        type: 'notification',
      });
    }
  });

  // Order cashout cancel

  Bus.on(Bus.events.history.orderCashoutCancelError, error => {
    NotificationManager.display({
      message: `order_cashout_cancel_error_${error.code}`,
      status: 'error',
      type: 'notification',
    });
  });

  Bus.on(Bus.events.bonus.bonusInstanceStatusChanged, ({ data }) => {
    NotificationManager.toast({
      data: { bonus: data.bonusName },
      type: 'bonusActivationSuccess',
    });
  });

  Bus.on(Bus.events.config.resolved, () => {
    (() =>
      setTimeout(() => {
        const isBetBoosterActive = SiteConfigManager.getConfig('config.config.betBooster.isActive');

        if (isBetBoosterActive) {
          const script = document.createElement('script');
          script.type = 'text/javascript';
          script.src = 'https://betbooster-widget.lvision.io/v2/prod/bundle-es5.js';
          script.defer = true;

          document.head.appendChild(script);
        }
      }, 10000))();
  });

  Bus.once('preorder:resolved', () => {
    let initialBetItemsCount = 0;

    const betslipChangeHandler = ({ betItems }) => {
      if (!initialBetItemsCount) {
        initialBetItemsCount = betItems.length;
      } else {
        history.push(window.location.pathname);

        Bus.off(`betslip:main:${Bus.events.bet.snapshot}`, betslipChangeHandler);
      }
    };

    Bus.on(`betslip:main:${Bus.events.bet.snapshot}`, betslipChangeHandler);
  });
};
